import "../reportDashboard.scss";

const RADIUS = 40;
const CIRC = 2 * Math.PI * RADIUS;
const GRADES = ["A", "B", "C", "D", "F"];

// Grade → semantic tone. Mirrors the mapping the ring's own is-grade{A..F}
// arc colours already use, so the banner tint and the headline text read as
// the same signal as the ring.
const GRADE_TONE = { A: "kudos", B: "accent", C: "warning", D: "danger", F: "danger" };

// Bar tone: four bands so a dimension at 65% doesn't render identically to
// one at 10% (every bar rendering red regardless of how close to passing it
// was is the screenshot bug this fixes). Same 4-band shape as UsersSection's
// engagementTone, different thresholds — deliberately not reused verbatim.
const barTone = (component) => {
  if (!component.max) return "muted";
  const pct = (component.score / component.max) * 100;
  if (pct >= 90) return "kudos";
  if (pct >= 70) return "warning";
  if (pct >= 50) return "corrective";
  return "danger";
};

// Horizontal banner: ring on the left (vertically centred beside the bars —
// see .rdScoreBanner in reportDashboard.scss), eyebrow/headline/dims copy and
// component bars filling the remaining space on the right — always visible,
// no expand-on-click gate. The banner is self-titled and grade-tinted, so
// callers must not wrap it in another rdCard + "Safety Score" title.
const SafetyScoreRing = ({
  score,
  grade,
  components = [],
  capNote,
  activeDimensions,
  totalDimensions,
  isScorable = true,
  unscorableNote,
}) => {
  // An org with nothing measurable in range has NO score. Rendering 0 would read as
  // "failing" and the old model rendered 85 ("Grade B for having no data"), so the
  // unscorable case gets its own treatment: an empty muted ring, a dash instead of a
  // number, no grade glyph, and the reason in place of the headline. Every component
  // row still renders as N/A so the reader can see exactly what is missing.
  const scorable = isScorable !== false && score !== null && score !== undefined;
  const safeScore = scorable ? Number(score) || 0 : 0;
  const dash = scorable ? CIRC * (Math.min(100, Math.max(0, safeScore)) / 100) : 0;
  const gradeClass = scorable && GRADES.includes(grade) ? ` is-grade${grade}` : "";
  const gradeTone = scorable ? GRADE_TONE[grade] || "muted" : "muted";

  const missingLabels = components
    .filter((c) => c.isNotApplicable)
    .map((c) => c.label);

  const dimsText = !scorable
    ? null
    : totalDimensions > 0
      ? missingLabels.length > 0
        ? `Weighted across ${activeDimensions} of ${totalDimensions} safety dimensions — no data in range for ${missingLabels.join(" and ")}`
        : `Weighted across ${totalDimensions} safety dimensions based on live data`
      : null;

  return (
    <div className={`rdScoreBanner${gradeClass}${scorable ? "" : " is-unscorable"}`}>
      <div className={`rdRing${gradeClass}`}>
        <svg width="110" height="110" viewBox="0 0 100 100">
          <circle className="rdRing__track" cx="50" cy="50" r={RADIUS} strokeWidth="10" />
          {/* A circle path starts at 3 o'clock, and the quarter-circumference
              dash offset below walks the arc's start back to 12 o'clock on its
              own. An additional rotate(-90) was compounding with it and
              starting the arc at 9 o'clock instead. Butt caps, not round: at a
              low score the arc is short, and round caps add half the 10px
              stroke to each end — enough to overstate a single-digit score. */}
          <circle
            className="rdRing__arc"
            cx="50"
            cy="50"
            r={RADIUS}
            strokeWidth="10"
            strokeDashoffset={CIRC * 0.25}
            style={{ "--rd-dash": `${dash} ${CIRC - dash}` }}
          />
          {/* Baselines are pulled in slightly from v26's 44/58/72. The "/" in
              "/ 100" is a full-height glyph that overshoots its 8px baseline in
              both directions, and against the grade's 18px cap height directly
              below it the two collided. */}
          {scorable ? (
            <>
              <text className="rdRing__score" x="50" y="43" textAnchor="middle" fontSize="26">
                {safeScore}
              </text>
              <text className="rdRing__unit" x="50" y="56" textAnchor="middle" fontSize="8">
                / 100
              </text>
              <text className="rdRing__grade" x="50" y="73" textAnchor="middle" fontSize="18">
                {grade}
              </text>
            </>
          ) : (
            /* Single centred glyph, so the three stacked baselines above (43/56/73)
               cannot collide with each other or with the ring stroke. */
            <text className="rdRing__score" x="50" y="58" textAnchor="middle" fontSize="26">
              &#8212;
            </text>
          )}
        </svg>
      </div>

      <div className="rdScoreBanner__body">
        <div className="rdScoreBanner__eyebrow">Safety Score</div>
        <div className={`rdScoreBanner__headline is-${gradeTone}`}>
          {scorable ? (
            <>
              {safeScore} / 100{" · "}Grade {grade}
            </>
          ) : (
            "No Safety Score"
          )}
        </div>
        {dimsText && <div className="rdScoreBanner__dims">{dimsText}</div>}
        {/* Reason stands in for the dims line, which is null when unscorable, so exactly
            one of the two ever renders and the banner keeps its normal vertical rhythm. */}
        {!scorable && (
          <div className="rdScoreBanner__dims">
            {unscorableNote || "Not enough data in this range to calculate a Safety Score"}
          </div>
        )}

        {capNote && (
          <div className="rdNotice rdNotice--danger">
            {"⚠ "}
            {capNote}
          </div>
        )}

        <div className="rdScoreBanner__components">
          {components.map((c) =>
            c.isNotApplicable ? (
              <div className="rdScoreBanner__row" key={c.label}>
                <div className="rdScoreBanner__rowHead">
                  <span>{c.label}</span>
                  <span className="rdValue is-muted">N/A</span>
                </div>
                <div className="rdBar rdBar--full">
                  <div className="rdBar__fill rdBar__fill--hatched" style={{ "--rd-fill": "100%" }} />
                </div>
                <div className="rdScoreBanner__rowDetail">{c.detail}</div>
              </div>
            ) : (
              <div className="rdScoreBanner__row" key={c.label}>
                <div className="rdScoreBanner__rowHead">
                  <span>
                    {c.label}
                    {c.isBonus && <span className="rdFlag is-kudos">bonus</span>}
                  </span>
                  <span className={`rdValue is-${barTone(c)}`}>
                    {c.isBonus ? "+" : ""}
                    {c.score}/{c.max}
                  </span>
                </div>
                <div className="rdBar rdBar--full">
                  <div
                    className={`rdBar__fill is-${barTone(c)}`}
                    style={{ "--rd-fill": `${c.max ? (c.score / c.max) * 100 : 0}%` }}
                  />
                </div>
                <div className="rdScoreBanner__rowDetail">{c.detail}</div>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default SafetyScoreRing;
